/**
 * `Intl.DateTimeFormat` bound to `locale` — no manual date math, no `moment`
 * (§20.3).
 *
 * Coerces its input instead of passing it straight to `Intl`: WS payloads
 * are typed as `Date` (`z.coerce.date()` in @xdocs/schemas) but
 * `requestTyped` is compile-time-only, so at call sites that skip a runtime
 * `.parse` the value is really an ISO STRING — and `Intl`'s `format()`
 * coerces a string via `ToNumber` → `NaN` → throws `RangeError: Invalid
 * time value`, which (absent an error boundary) white-screened whole pages
 * the first time real data flowed (see `useJobSnapshot`/`useVersionHistory`).
 * A display formatter must degrade (render "—"), never crash the tree; the
 * REAL fix stays parse-at-boundary at each hook, this is the last line.
 */
export function formatDate(locale, date, opts) {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime()))
        return '—';
    return new Intl.DateTimeFormat(locale, opts).format(value);
}
/** `Intl.NumberFormat` bound to `locale`. */
export function formatNumber(locale, value, opts) {
    return new Intl.NumberFormat(locale, opts).format(value);
}
/** `Intl.RelativeTimeFormat` bound to `locale`, e.g. `formatRelativeTime('en', -2, 'day')` → "2 days ago". */
export function formatRelativeTime(locale, value, unit, opts) {
    return new Intl.RelativeTimeFormat(locale, opts).format(value, unit);
}
const fractionDigitsCache = new Map();
function currencyFractionDigits(locale, currency) {
    const cacheKey = `${locale}:${currency}`;
    const cached = fractionDigitsCache.get(cacheKey);
    if (cached !== undefined)
        return cached;
    const digits = new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits;
    const resolved = digits ?? 2;
    fractionDigitsCache.set(cacheKey, resolved);
    return resolved;
}
/**
 * Formats an integer **minor-units** amount (cents) + ISO currency code as
 * locale-formatted currency (§20.3). Money is NEVER pre-formatted server
 * side — the server sends `{ minorUnits, currency }` and this is the one
 * place that turns it into a display string, so grouping/decimal/symbol
 * placement always match the active locale (RO `1.234,56 RON`, en-US
 * `$1,234.56`), independent of the currency's own decimal precision (e.g.
 * JPY has 0 fraction digits).
 */
export function formatCurrency(locale, minorUnits, currency) {
    if (!Number.isInteger(minorUnits)) {
        throw new TypeError(`formatCurrency expects integer minor units, got ${minorUnits}`);
    }
    const digits = currencyFractionDigits(locale, currency);
    const amount = minorUnits / 10 ** digits;
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}
/** CLDR-sanctioned digital-storage unit identifiers, smallest→largest. */
const BYTE_UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
/**
 * Formats a byte count as a locale-correct, unit-scaled string via
 * `Intl.NumberFormat({ style: 'unit' })` (§20.3). `Intl` has no built-in
 * "pick the right magnitude" behavior for byte counts, so the 1024-scaling
 * loop is the one piece of manual logic here; the unit LABEL and the
 * decimal separator are always resolved through `Intl`, never a hardcoded
 * English string or `.toFixed()`. THE single helper for byte-size display —
 * replaces the `formatBytes` duplicated in explorer/upload components.
 */
export function formatByteSize(locale, bytes) {
    if (!Number.isFinite(bytes) || bytes < 0) {
        throw new TypeError(`formatByteSize expects a non-negative finite byte count, got ${bytes}`);
    }
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }
    const unit = BYTE_UNITS[unitIndex] ?? 'terabyte';
    const fractionDigits = unitIndex === 0 ? 0 : 1;
    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(value);
}
